import {
	Copy,
	FileText,
	Group as LucideGroup,
	ListChevronsDownUp,
	type LucideIcon,
	PhoneIcon,
	SquaresUnite,
	User,
} from "lucide-react";

import adobeIcon from "@/assets/applications/AdobeIcon.svg";
import calendarIcon from "@/assets/applications/CalendarIcon.svg";
import chromeIcon from "@/assets/applications/ChromeIcon.svg";
import excelIcon from "@/assets/applications/ExcelIcon.svg";
import foxitIcon from "@/assets/applications/FoxitIcon.svg";
import kofaxIcon from "@/assets/applications/KofaxIcon.svg";
import outlookIcon from "@/assets/applications/OutlookIcon.svg";
import photosIcon from "@/assets/applications/WindowsPhotos.svg";
import powerpointIcon from "@/assets/applications/PowerpointIcon.svg";
import teamsIcon from "@/assets/applications/TeamsIcon.svg";
import wordIcon from "@/assets/applications/WordIcon.svg";
import zoomIcon from "@/assets/applications/ZoomIcon.svg";

type IconEntry =
	| { kind: "svg"; src: string }
	| { kind: "lucide"; Icon: LucideIcon };

const APP_ICON_REGISTRY: Record<string, IconEntry> = {
	"Microsoft Word": { kind: "svg", src: wordIcon },
	Word: { kind: "svg", src: wordIcon },
	"Microsoft Outlook": { kind: "svg", src: outlookIcon },
	Outlook: { kind: "svg", src: outlookIcon },
	"Microsoft Outlook Calendar": { kind: "svg", src: calendarIcon },
	"Microsoft Excel": { kind: "svg", src: excelIcon },
	Excel: { kind: "svg", src: excelIcon },
	"Microsoft Powerpoint": { kind: "svg", src: powerpointIcon },
	Powerpoint: { kind: "svg", src: powerpointIcon },
	"Microsoft Teams": { kind: "svg", src: teamsIcon },
	Teams: { kind: "svg", src: teamsIcon },
	"MS Teams": { kind: "svg", src: teamsIcon },
	"Teams Meeting": { kind: "svg", src: teamsIcon },
	"Microsoft Teams Meeting": { kind: "svg", src: teamsIcon },
	"TEAMS.EXE": { kind: "svg", src: teamsIcon },
	"MS-TEAMS.EXE": { kind: "svg", src: teamsIcon },
	"Adobe Acrobat": { kind: "svg", src: adobeIcon },
	"Google Chrome": { kind: "svg", src: chromeIcon },
	Chrome: { kind: "svg", src: chromeIcon },
	Zoom: { kind: "svg", src: zoomIcon },
	Kofax: { kind: "svg", src: kofaxIcon },
	Foxit: { kind: "svg", src: foxitIcon },
	Photos: { kind: "svg", src: photosIcon },
	Phone: { kind: "lucide", Icon: PhoneIcon },
	"User Input": { kind: "lucide", Icon: User },
	Group: { kind: "lucide", Icon: LucideGroup },
	Duplicate: { kind: "lucide", Icon: Copy },
	Merge: { kind: "lucide", Icon: SquaresUnite },
	Collapse: { kind: "lucide", Icon: ListChevronsDownUp },
};

export interface AppIconProps {
	application?: string | null;
	className?: string;
}

/**
 * Renders the canonical icon for a backend `app_name`. Branded apps (Word,
 * Outlook, ...) render the SVG asset; action and manual-source markers
 * (Group, Merge, ...) render the matching lucide icon used in the toolbar.
 * Unknown app names fall back to a generic file icon.
 */
export function AppIcon({ application, className }: AppIconProps) {
	const entry = application ? APP_ICON_REGISTRY[application] : undefined;
	if (!entry) {
		return <FileText className={className} aria-label={application ?? "Unknown app"} />;
	}
	if (entry.kind === "svg") {
		return <img src={entry.src} alt={application ?? ""} className={className} />;
	}
	const Icon = entry.Icon;
	return <Icon className={className} aria-label={application ?? undefined} />;
}
